/**
 * Optimized API Client with caching, request deduplication, and batching
 * Professional separation of concerns for fast data fetching
 */

// Request cache with TTL
const requestCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Active requests map to prevent duplicate requests
const activeRequests = new Map();

/**
 * Get cached response if available and not expired
 */
const getCachedResponse = (key) => {
  const cached = requestCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  if (cached) {
    requestCache.delete(key);
  }
  return null;
};

/**
 * Cache a response
 */
const cacheResponse = (key, data) => {
  requestCache.set(key, {
    data,
    timestamp: Date.now(),
  });
};

/**
 * Clear cache for a specific key or all cache
 */
export const clearCache = (key = null) => {
  if (key) {
    requestCache.delete(key);
  } else {
    requestCache.clear();
  }
};

/**
 * Get auth headers
 */
const getAuthHeaders = async () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Optimized fetch with caching, deduplication, and error handling
 */
export const apiRequest = async (url, options = {}) => {
  const {
    method = 'GET',
    body,
    useCache = true,
    skipAuth = false,
    timeout = 15000, // Increased to 15 seconds for slower connections
  } = options;

  // Create cache key
  const cacheKey = `${method}:${url}:${body ? JSON.stringify(body) : ''}`;

  // Return cached response if available
  if (useCache && method === 'GET') {
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      // Refresh in background
      apiRequest(url, { ...options, useCache: false }).then((result) => {
        if (result && !result.error) {
          cacheResponse(cacheKey, result);
        }
      }).catch(() => {
        // Ignore background refresh errors
      });
      return cached;
    }
  }

  // Check if request is already in progress
  if (activeRequests.has(cacheKey)) {
    return activeRequests.get(cacheKey);
  }

  // Create request promise
  const requestPromise = (async () => {
    try {
      const headers = skipAuth ? { 'Content-Type': 'application/json' } : await getAuthHeaders();
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const fetchOptions = {
        method,
        headers,
        signal: controller.signal,
      };

      if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Request failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Cache successful GET requests
      if (useCache && method === 'GET' && result && !result.error) {
        cacheResponse(cacheKey, result);
      }

      return result;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    } finally {
      // Remove from active requests
      activeRequests.delete(cacheKey);
    }
  })();

  // Store active request
  activeRequests.set(cacheKey, requestPromise);

  return requestPromise;
};

/**
 * Batch multiple requests into parallel execution
 */
export const batchRequests = async (requests) => {
  return Promise.allSettled(requests.map(req => 
    typeof req === 'function' ? req() : req
  ));
};

/**
 * Parallel fetch multiple URLs
 */
export const fetchParallel = async (urls, options = {}) => {
  const requests = urls.map(url => apiRequest(url, options));
  const results = await Promise.allSettled(requests);
  
  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return { url: urls[index], data: result.value, error: null };
    }
    return { url: urls[index], data: null, error: result.reason };
  });
};

/**
 * Get user data with caching
 */
export const getUserData = async (useCache = true) => {
  return apiRequest('/api/user', { useCache });
};

/**
 * Get balance with caching
 */
export const getBalanceData = async (useCache = true) => {
  return apiRequest('/api/balance', { useCache, timeout: 20000 }); // 20 seconds for balance
};

/**
 * Get cart with caching
 */
export const getCartData = async (useCache = true) => {
  return apiRequest('/api/cart', { useCache, timeout: 20000 }); // 20 seconds for cart
};

/**
 * Get ad details with caching
 */
export const getAdDetails = async (adId, useCache = true) => {
  return apiRequest(`/api/ads/${adId}`, { useCache, skipAuth: true });
};

/**
 * Get multiple ads in parallel
 */
export const getAdsParallel = async (adIds, useCache = true) => {
  const urls = adIds.map(id => `/api/ads/${id}`);
  return fetchParallel(urls, { useCache, skipAuth: true });
};

